import { create } from 'zustand';

import { getAccessToken, getServiceId } from '@/api/tokens.helper';
import { useAuth } from '@/store/auth/auth.store';
import { useChats } from '@/store/chats/chats.store';
import { useMessages } from '@/store/messages/messages.store';
import { usePerson } from '@/store/persons/person.store';
import { randomId } from '@/utils/random-id.ts';

import type {
  TypingUser,
  WSConnectionStatus,
  WSEventType,
  WSMediaFileUpdated,
  WSMessage,
  WSMessageModel,
} from './websocket.dto';
import { WS_EVENT_TYPES } from './websocket.dto';

/** The messages with `update`'s file in them changed to match it; the rest as they were. */
const withMediaUpdate = <
  F extends { id: string },
  M extends { id: string; media_files?: F[] | null },
>(
  messages: M[],
  update: WSMediaFileUpdated,
  patch: (file: F) => F,
): M[] =>
  messages.map((message) =>
    message.id === update.message_id && message.media_files?.some((f) => f.id === update.id)
      ? {
          ...message,
          media_files: message.media_files.map((f) => (f.id === update.id ? patch(f) : f)),
        }
      : message,
  );

export type PendingMessageAttachment = {
  id: string;
  name: string;
  localUrl: string;
  type: string;
  size: number;
  isImage: boolean;
};

export type PendingMessage = {
  tid: string;
  text: string;
  roomId: string;
  createdAt: string;
  creatorId: string;
  status: 'sending' | 'failed';
  quoteMessageId?: string;
  mentions?: Record<string, string>;
  attachments?: PendingMessageAttachment[];
};

const WS_URL = import.meta.env.VITE_WS_CHAT_URL || 'ws://3.9.175.158/api/v1/chat/ws';

type WebSocketState = {
  connection: WebSocket | null;
  connectionStatus: WSConnectionStatus;
  currentRoomId: string | null;

  messages: Record<string, WSMessageModel[]>;
  readMessageIds: Record<string, string[]>;
  pendingMessages: PendingMessage[];
  typingUsers: TypingUser[];
  notifications: WSMessage[];

  connect: () => void;
  disconnect: () => void;
  subscribe: (roomId: string) => void;
  unsubscribe: () => void;
  sendMessage: (text: string, quoteMessageId?: string, mentions?: Record<string, string>) => string; // returns tid
  editMessage: (messageId: string, text: string, quoteMessageId?: string) => void;
  deleteMessage: (messageId: string) => void;
  sendTypingIndicator: () => void;
  markMessageFailed: (tid: string) => void;
  retryMessage: (tid: string) => void;
  removePendingMessage: (tid: string) => void;
  addFilePendingMessage: (
    tid: string,
    roomId: string,
    files: File[],
    text: string,
    creatorId: string,
    /** One object URL per file, in the same order. */
    localUrls: string[],
  ) => void;

  _handleMessage: (event: MessageEvent) => void;
  _handleOpen: () => void;
  _handleClose: (event: CloseEvent) => void;
  _handleError: (event: Event) => void;
  _send: <T>(message: WSMessage<T>) => void;
  _startHeartbeat: () => void;
  _stopHeartbeat: () => void;
};

let typingTimer: ReturnType<typeof setTimeout> | null = null;
const TYPING_DEBOUNCE_MS = 2000;

let heartbeatInterval: ReturnType<typeof setInterval> | null = null;
let pongTimeout: ReturnType<typeof setTimeout> | null = null;
const HEARTBEAT_INTERVAL_MS = 25000;
const PONG_TIMEOUT_MS = 10000;
const SEND_ACK_TIMEOUT_MS = 15000;

export const useWebSocket = create<WebSocketState>((set, get) => ({
  connection: null,
  connectionStatus: 'disconnected',
  currentRoomId: null,
  messages: {},
  readMessageIds: {},
  pendingMessages: [],
  typingUsers: [],
  notifications: [],

  connect: () => {
    const token = getAccessToken();
    if (!token) {
      console.error('[WS] No access token available');
      set({ connectionStatus: 'error' });
      return;
    }

    const serviceId = getServiceId();

    const existing = get().connection;
    // Skip if a socket is already open or still opening. A second socket would orphan the first,
    // whose late onopen then fires _handleOpen and sends on the wrong (CONNECTING) connection.
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN || existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    try {
      set({ connectionStatus: 'connecting' });
      const params = new URLSearchParams({ jwt: token });
      if (serviceId) {
        params.append('service_id', serviceId);
      }
      const wsUrl = `${WS_URL}?${params.toString()}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = get()._handleOpen;
      ws.onclose = get()._handleClose;
      ws.onerror = get()._handleError;
      ws.onmessage = get()._handleMessage;

      set({ connection: ws });
    } catch (error) {
      console.error('[WS] Connection error:', error);
      set({ connectionStatus: 'error' });
    }
  },

  disconnect: () => {
    const { connection, currentRoomId } = get();
    if (connection) {
      get()._stopHeartbeat();
      connection.close();
      set({
        connection: null,
        connectionStatus: 'disconnected',
        currentRoomId: null,
        typingUsers: currentRoomId
          ? get().typingUsers.filter((u) => u.roomId !== currentRoomId)
          : [],
      });
    }
  },

  subscribe: (roomId: string) => {
    const { currentRoomId, _send } = get();

    if (currentRoomId && currentRoomId !== roomId) {
      get().unsubscribe();
    }

    set({ currentRoomId: roomId });

    const subscribeMessage = {
      type: WS_EVENT_TYPES.SUBSCRIBE,
      data: { room_id: roomId },
    };

    _send(subscribeMessage);
  },

  unsubscribe: () => {
    const { currentRoomId } = get();
    if (currentRoomId) {
      set({
        currentRoomId: null,
        typingUsers: get().typingUsers.filter((u) => u.roomId !== currentRoomId),
      });
    }
  },

  sendMessage: (text: string, quoteMessageId?: string, mentions?: Record<string, string>) => {
    const { currentRoomId, connectionStatus } = get();
    if (!currentRoomId) return '';

    const tid = randomId();
    const currentUserId = useAuth.getState().session?.id || '';

    const pendingMessage: PendingMessage = {
      tid,
      text,
      roomId: currentRoomId,
      createdAt: new Date().toISOString(),
      creatorId: currentUserId,
      status: connectionStatus === 'connected' ? 'sending' : 'failed',
      quoteMessageId,
      mentions,
    };

    set((state) => ({
      pendingMessages: [...state.pendingMessages, pendingMessage],
    }));

    if (connectionStatus === 'connected') {
      get()._send({
        type: WS_EVENT_TYPES.NEW_MESSAGE,
        data: {
          text,
          tid,
          room_id: currentRoomId,
          quote_message_id: quoteMessageId || null,
          mentions: mentions ?? {},
        },
      });

      setTimeout(() => {
        const stuck = get().pendingMessages.find(
          (msg) => msg.tid === tid && msg.status === 'sending',
        );
        if (!stuck) return;

        console.error('[WS] no ack for message', tid);
        set((state) => ({
          pendingMessages: state.pendingMessages.map((msg) =>
            msg.tid === tid ? { ...msg, status: 'failed' } : msg,
          ),
        }));
      }, SEND_ACK_TIMEOUT_MS);
    }

    return tid;
  },

  editMessage: (messageId: string, text: string, quoteMessageId?: string) => {
    get()._send({
      type: WS_EVENT_TYPES.EDIT_MESSAGE,
      data: {
        message_id: messageId,
        text,
        quote_message_id: quoteMessageId || null,
      },
    });
  },

  deleteMessage: (messageId: string) => {
    get()._send({
      type: WS_EVENT_TYPES.DELETE_MESSAGE,
      data: {
        message_id: messageId,
      },
    });
  },

  sendTypingIndicator: () => {
    if (typingTimer) return; // Already sent recently

    get()._send({
      type: WS_EVENT_TYPES.WRITING_MESSAGE,
      data: {},
    });

    typingTimer = setTimeout(() => {
      typingTimer = null;
    }, TYPING_DEBOUNCE_MS);
  },

  markMessageFailed: (tid: string) => {
    set((state) => ({
      pendingMessages: state.pendingMessages.map((msg) =>
        msg.tid === tid ? { ...msg, status: 'failed' } : msg,
      ),
    }));
  },

  retryMessage: (tid: string) => {
    const { pendingMessages, connectionStatus } = get();
    const msg = pendingMessages.find((m) => m.tid === tid);
    if (!msg || connectionStatus !== 'connected') return;

    set((state) => ({
      pendingMessages: state.pendingMessages.map((m) =>
        m.tid === tid ? { ...m, status: 'sending' } : m,
      ),
    }));

    get()._send({
      type: WS_EVENT_TYPES.NEW_MESSAGE,
      data: {
        text: msg.text,
        tid: msg.tid,
        room_id: msg.roomId,
        quote_message_id: msg.quoteMessageId || null,
        mentions: msg.mentions ?? {},
      },
    });
  },

  removePendingMessage: (tid: string) => {
    set((state) => ({
      pendingMessages: state.pendingMessages.filter((msg) => msg.tid !== tid),
    }));
  },

  addFilePendingMessage: (tid, roomId, files, text, creatorId, localUrls) => {
    const pendingMessage: PendingMessage = {
      tid,
      text,
      roomId,
      createdAt: new Date().toISOString(),
      creatorId,
      status: 'sending',
      attachments: files.map((file, index) => ({
        id: `${tid}-${index}`,
        name: file.name,
        localUrl: localUrls[index],
        type: file.type,
        size: file.size,
        isImage: file.type.startsWith('image/'),
      })),
    };
    set((state) => ({
      pendingMessages: [...state.pendingMessages, pendingMessage],
    }));
  },

  // Internal handlers
  _handleOpen: () => {
    set({ connectionStatus: 'connected' });

    // Subscribe to global notifications (ASSIGN_USER, UNASSIGN_USER, etc.)
    get()._send({
      type: WS_EVENT_TYPES.SUBSCRIBE_NOTIFICATION,
      data: {},
    });

    const { currentRoomId } = get();
    if (currentRoomId) {
      get()._send({
        type: WS_EVENT_TYPES.SUBSCRIBE,
        data: { room_id: currentRoomId },
      });
    }

    get()._startHeartbeat();
  },

  _handleClose: (event: CloseEvent) => {
    // Ignore a stale socket's close so it can't null out a freshly-built connection after a reconnect.
    if (event?.target && event.target !== get().connection) return;
    get()._stopHeartbeat();
    set({ connectionStatus: 'disconnected', connection: null });
  },

  _handleError: (event: Event) => {
    console.error('[WS] Connection error');
    console.error('[WS] Error event:', event);
    set({ connectionStatus: 'error' });
  },

  _handleMessage: (event: MessageEvent) => {
    // Any inbound message proves the socket is alive — cancel the pending heartbeat timeout.
    if (pongTimeout) {
      clearTimeout(pongTimeout);
      pongTimeout = null;
    }

    try {
      const response = JSON.parse(event.data) as WSMessage;
      const { type, data } = response;

      switch (type as WSEventType) {
        case WS_EVENT_TYPES.SUBSCRIBE:
        case WS_EVENT_TYPES.PONG: {
          break;
        }

        case WS_EVENT_TYPES.NEW_MESSAGE: {
          const { message, tid } = data as { message: WSMessageModel; tid: string };
          const roomId = message.room_id;

          set((state) => ({
            messages: {
              ...state.messages,
              [roomId]: [...(state.messages[roomId] || []), message],
            },
            pendingMessages: state.pendingMessages.filter((pending) => {
              if (tid) return pending.tid !== tid;
              if (pending.roomId !== roomId) return true;
              return pending.text.trim() !== (message.text ?? '').trim();
            }),
          }));

          if (message.media_files?.length) {
            void useMessages.getState().getMessages(roomId, 100, false);
          }

          try {
            const currentUserId = useAuth.getState().session?.id;
            const rooms = useChats.getState().chatRooms ?? [];
            const exists = rooms.some((room) => room.id === roomId);

            if (!exists) {
              useChats
                .getState()
                .getChatRooms()
                .catch((err) => {
                  console.error('[WS] Failed to refresh chat rooms', err);
                });
            } else {
              const lastMessage = {
                id: message.id,
                text: message.text,
                status: 'sent',
                created_at: message.created_at,
                creator_id: message.creator_id,
                room_id: message.room_id,
                media_files: message.media_files,
                mentions: message.mentions,
              };

              useChats.setState((chatState) => {
                const nextRooms = (chatState.chatRooms ?? []).map((room) =>
                  room.id === roomId
                    ? {
                        ...room,
                        last_message: lastMessage,
                        room_unread_messages:
                          message.creator_id && message.creator_id !== currentUserId
                            ? (room.room_unread_messages ?? 0) + 1
                            : (room.room_unread_messages ?? 0),
                      }
                    : room,
                );

                return { chatRooms: nextRooms };
              });
            }
          } catch (err) {
            console.error('[WS] failed to sync chatRooms with new message', err);
          }
          break;
        }

        case WS_EVENT_TYPES.EDIT_MESSAGE: {
          const { message } = data as { message: WSMessageModel };
          const roomId = message.room_id;

          set((state) => ({
            messages: {
              ...state.messages,
              [roomId]: (state.messages[roomId] || []).map((m) =>
                m.id === message.id ? message : m,
              ),
            },
          }));

          const editedAt = message.updated_at ?? new Date().toISOString();
          useMessages.setState((state) => ({
            messages:
              state.messages?.map((m) =>
                m.id === message.id ? { ...m, text: message.text, updated_at: editedAt } : m,
              ) ?? null,
            messagesByRoom: {
              ...state.messagesByRoom,
              [roomId]: (state.messagesByRoom[roomId] ?? []).map((m) =>
                m.id === message.id ? { ...m, text: message.text, updated_at: editedAt } : m,
              ),
            },
            pinnedMessages:
              state.pinnedMessages?.map((m) =>
                m.id === message.id ? { ...m, text: message.text, updated_at: editedAt } : m,
              ) ?? null,
          }));
          break;
        }

        case WS_EVENT_TYPES.MEDIA_FILE_UPDATED: {
          const update = data as WSMediaFileUpdated;
          const roomId = update.room_id;
          const fields = {
            media_status: update.media_status,
            file_name: update.file_name,
            size: update.size,
            variants: update.variants ?? {},
          };

          set((state) => ({
            messages: {
              ...state.messages,
              [roomId]: withMediaUpdate(state.messages[roomId] ?? [], update, (f) => ({
                ...f,
                ...fields,
              })),
            },
          }));

          const patch = <F extends { id: string }>(f: F) => ({
            ...f,
            ...fields,
            type: update.type,
          });
          useMessages.setState((state) => ({
            messages: state.messages && withMediaUpdate(state.messages, update, patch),
            messagesByRoom: {
              ...state.messagesByRoom,
              [roomId]: withMediaUpdate(state.messagesByRoom[roomId] ?? [], update, patch),
            },
            pinnedMessages:
              state.pinnedMessages && withMediaUpdate(state.pinnedMessages, update, patch),
            bidMessages: state.bidMessages && withMediaUpdate(state.bidMessages, update, patch),
          }));
          break;
        }

        case WS_EVENT_TYPES.DELETE_MESSAGE: {
          const { message } = data as { message: { id: string } };
          const currentRoomId = get().currentRoomId;

          if (currentRoomId) {
            set((state) => ({
              messages: {
                ...state.messages,
                [currentRoomId]: (state.messages[currentRoomId] || []).filter(
                  (m) => m.id !== message.id,
                ),
              },
            }));
          }

          useMessages.setState((state) => ({
            messages: state.messages?.filter((m) => m.id !== message.id) ?? null,
            messagesByRoom: Object.fromEntries(
              Object.entries(state.messagesByRoom).map(([room, roomMessages]) => [
                room,
                roomMessages.filter((m) => m.id !== message.id),
              ]),
            ),
            pinnedMessages: state.pinnedMessages?.filter((m) => m.id !== message.id) ?? null,
          }));

          if (currentRoomId) {
            void useMessages.getState().getMessages(currentRoomId, 100, false);
          }
          void useChats.getState().getChatRooms();
          break;
        }

        case WS_EVENT_TYPES.MESSAGES_READ:
        case WS_EVENT_TYPES.READ_MESSAGE: {
          const payload = data as {
            room_id?: string;
            message_ids?: string[];
            message_id?: string;
            message?: { id?: string; room_id?: string; creator_id?: string };
            messages?: { id?: string; room_id?: string }[];
            user_id?: string;
            user?: { id?: string };
          };

          const room_id =
            payload.room_id ?? payload.message?.room_id ?? payload.messages?.[0]?.room_id;
          if (!room_id) break;

          const message_ids = [
            ...(payload.message_ids ?? []),
            ...(payload.message_id ? [payload.message_id] : []),
            ...(payload.message?.id ? [payload.message.id] : []),
            ...(payload.messages ?? []).flatMap((item) => (item?.id ? [item.id] : [])),
          ];
          const { user_id, user } = payload;
          const liveMessages = get().messages[room_id] ?? [];
          const storedMessages = useMessages.getState().messagesByRoom[room_id] ?? [];
          const knownMessages = [...liveMessages, ...storedMessages];
          const ids = message_ids.length
            ? Array.from(new Set(message_ids))
            : Array.from(new Set(knownMessages.map((item) => item.id)));

          useMessages.getState().markMessagesReadLocally(room_id, ids);

          set((state) => ({
            readMessageIds: {
              ...state.readMessageIds,
              [room_id]: Array.from(new Set([...(state.readMessageIds[room_id] ?? []), ...ids])),
            },
          }));

          const currentUserId = useAuth.getState().session?.id;
          const readerId = user_id ?? user?.id;
          const readIds = new Set(ids);
          const readSomeoneElsesMessages = knownMessages.some(
            (item) => readIds.has(item.id) && item.creator_id !== currentUserId,
          );
          const readByMe = readerId ? readerId === currentUserId : readSomeoneElsesMessages;

          if (readByMe) {
            useChats.getState().markRoomAsReadLocally(room_id);
          }
          break;
        }

        case WS_EVENT_TYPES.WRITING_MESSAGE: {
          const { user, room_id } = data as { user: { id: string; name: string }; room_id: string };

          const typingUser: TypingUser = {
            userId: user.id,
            userName: user.name,
            roomId: room_id,
          };

          set((state) => ({
            typingUsers: [...state.typingUsers.filter((u) => u.userId !== user.id), typingUser],
          }));

          setTimeout(() => {
            set((state) => ({
              typingUsers: state.typingUsers.filter((u) => u.userId !== user.id),
            }));
          }, 3000);
          break;
        }

        case WS_EVENT_TYPES.ASSIGN_USER: {
          useChats
            .getState()
            .getChatRooms()
            .catch((err) => {
              console.error('[WS] Failed to refresh chat rooms on ASSIGN_USER', err);
            });
          const assignRoomId = get().currentRoomId;
          if (assignRoomId) {
            useMessages.getState().getMessages(assignRoomId, 100, false);
          }
          break;
        }

        case WS_EVENT_TYPES.UNASSIGN_USER: {
          useChats
            .getState()
            .getChatRooms()
            .catch((err) => {
              console.error('[WS] Failed to refresh chat rooms on UNASSIGN_USER', err);
            });
          const unassignRoomId = get().currentRoomId;
          if (unassignRoomId) {
            useMessages.getState().getMessages(unassignRoomId, 100, false);
          }
          break;
        }

        case WS_EVENT_TYPES.SUBSCRIBE_NOTIFICATION: {
          void usePerson.getState().getActiveSubscription();
          break;
        }

        default:
          set({ notifications: [...get().notifications, response] });
          console.warn('[WS] Unknown message type:', type);
      }
    } catch (error) {
      console.error('[WS] Failed to parse message:', error);
    }
  },

  _send: <T>(message: WSMessage<T>) => {
    const { connection, connectionStatus } = get();

    if (!connection || connectionStatus !== 'connected') {
      console.error('[WS] Cannot send message: not connected');
      return;
    }

    try {
      connection.send(JSON.stringify(message));
    } catch (error) {
      console.error('[WS] Failed to send message:', error);
    }
  },

  // Detects a dead-but-OPEN socket: ping on an interval, and if no message arrives back within
  // PONG_TIMEOUT_MS, close it. The close fires onclose → reconnect. Any inbound message (pong or
  // real traffic) cancels the timeout in _handleMessage.
  _startHeartbeat: () => {
    get()._stopHeartbeat();

    heartbeatInterval = setInterval(() => {
      const { connection } = get();
      if (!connection || connection.readyState !== WebSocket.OPEN) {
        get()._stopHeartbeat();
        return;
      }

      if (pongTimeout) clearTimeout(pongTimeout);
      pongTimeout = setTimeout(() => {
        get()._stopHeartbeat();
        get().connection?.close();
      }, PONG_TIMEOUT_MS);

      try {
        connection.send(JSON.stringify({ type: WS_EVENT_TYPES.PING, data: {} }));
      } catch (error) {
        console.error('[WS] Heartbeat ping failed:', error);
      }
    }, HEARTBEAT_INTERVAL_MS);
  },

  _stopHeartbeat: () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
    if (pongTimeout) {
      clearTimeout(pongTimeout);
      pongTimeout = null;
    }
  },
}));
